import React, { useContext, useState, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getPortfolioSummary, syncHoldings } from "../../api/portfolioApi";
import StockTicker from "../../components/StockTicker";
import MarketStatus from "../../components/MarketStatus";
import SectorAllocationChart from "../../components/SectorAllocationChart";
import MiniSparkline from "../../components/MiniSparkline";
import TodayBriefCard from "../../components/TodayBriefCard";
import AskCouncilButton from "../../components/AskCouncilButton";
import useIsMobile from "../../hooks/useIsMobile";
import Button from "../../components/Button";
import LoadingLines from "../../components/LoadingLines";
import { useToast, ToastContainer } from "../../components/Toast";
import { AuthContext } from "../../context/AuthContext";
import {
  formatCurrency,
  resolveDisplayCurrency,
  sumFieldInCurrency,
  totalInCurrency,
} from "../../utils/currency";

/**
 * Recompute total value / cost / gain in the user's display currency from
 * per-holding native values. Replaces the old hard-coded USD conversion.
 */
const projectPortfolioInCurrency = (data, displayCurrency) => {
  const holdings = data?.holdings || [];
  if (!holdings.length) return data;
  const totalValue = totalInCurrency(holdings, displayCurrency);
  const totalCost = sumFieldInCurrency(holdings, "costBasis", displayCurrency);
  const totalReturn = totalValue - totalCost;
  const totalReturnPct = totalCost > 0 ? (totalReturn / totalCost) * 100 : 0;
  return {
    ...data,
    totalValue,
    totalReturn,
    totalReturnPercent: totalReturnPct,
    // We don't have intraday data, so today's change is the same number as
    // total gain — preserves the existing behaviour, just in the right currency.
    todayChange: totalReturn,
    todayChangePercent: totalReturnPct,
  };
};

const Overview = () => {
  const { profile } = useContext(AuthContext);
  const isMobile = useIsMobile();
  const displayCurrency = resolveDisplayCurrency(profile);
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'marketValue', direction: 'desc' });
  const { toasts, addToast, removeToast } = useToast();

  // Fetch portfolio data
  const fetchPortfolioData = useCallback(async () => {
    try {
      setError(null);
      const raw = await getPortfolioSummary();
      const data = projectPortfolioInCurrency(raw, displayCurrency);
      setPortfolioData(data);
    } catch (err) {
      console.error("Failed to fetch portfolio data:", err);
      
      // Check if it's an authentication error
      if (err.response && err.response.status === 401) {
        setError("Authentication required. Please log in.");
      } else if (err.response && err.response.status === 400) {
        setError("Bad request. Please try syncing your data.");
      } else {
        setError("Failed to load portfolio data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchPortfolioData();
  }, [fetchPortfolioData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPortfolioData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [fetchPortfolioData]);

  // When the user changes display_currency in Settings, re-project locally
  // without a refetch so values flip immediately.
  useEffect(() => {
    setPortfolioData((prev) =>
      prev ? projectPortfolioInCurrency(prev, displayCurrency) : prev,
    );
  }, [displayCurrency]);

  // Handle sync
  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncHoldings();
      await fetchPortfolioData();
      addToast("Portfolio data synced successfully", "success");
    } catch (err) {
      console.error("Sync failed:", err);
      setError("Failed to sync holdings");
      addToast("Failed to sync holdings", "error");
    } finally {
      setSyncing(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!sortedHoldings || sortedHoldings.length === 0) {
      addToast("No data to export", "warning");
      return;
    }

    const headers = ["Symbol", "Name", "Weight %", "Quantity", "Price", "Value", "P/L %"];
    const rows = sortedHoldings.map(h => [
      h.symbol,
      h.name,
      h.weight.toFixed(2),
      h.quantity.toFixed(2),
      h.price.toFixed(2),
      h.marketValue.toFixed(2),
      h.unrealizedPLPercent.toFixed(2)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `portfolio-holdings-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    addToast("Portfolio exported successfully", "success");
  };

  // Sort holdings
  const sortedHoldings = React.useMemo(() => {
    if (!portfolioData?.holdings) return [];
    
    let sortableHoldings = [...portfolioData.holdings];
    sortableHoldings.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sortableHoldings;
  }, [portfolioData, sortConfig]);

  // Request sort
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Generate mock chart data (in a real app, this would come from backend with historical data)
  const generateChartData = () => {
    const days = 30;
    const data = [];
    const today = new Date();
    const baseValue = portfolioData?.totalValue || 100000;
    const baseReturn = portfolioData?.totalReturnPercent || 0;
    
    for (let i = days; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const randomVariation = (Math.random() - 0.5) * 3;
      const portfolioValue = baseValue * (1 - (baseReturn/100) * (i/days)) * (1 + randomVariation/100);
      const sp500Value = baseValue * 0.95 * (1 + 10 * (1 - i/days)/100) * (1 + randomVariation/200);
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        portfolio: Math.round(portfolioValue),
        sp500: Math.round(sp500Value)
      });
    }
    return data;
  };

  const chartData = portfolioData ? generateChartData() : [];

  if (loading) {
    return <LoadingLines fullScreen />;
  }

  if (error && !portfolioData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Make sure you're logged in and have linked your brokerage account.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={fetchPortfolioData}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Retry
            </button>
            <a
              href="/settings?tab=connected"
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Link Account
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn pt-4">
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* ------------------ STOCK TICKER ------------------ */}
      {portfolioData?.holdings && portfolioData.holdings.length > 0 && (
        <StockTicker holdings={portfolioData.holdings} />
      )}

      {/* ------------------ TITLE & MARKET STATUS ------------------ */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl sm:text-4xl font-bold">Portfolio Analysis</h1>
          <p className="text-gray-700 dark:text-gray-400 mt-2 text-sm sm:text-base">
            Link your brokerage accounts, visualize performance vs. S&P 500, and let FinAI highlight
            risks, concentration, and opportunities automatically.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
            <MarketStatus />
            {portfolioData?.lastSynced && (
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Last synced: {new Date(portfolioData.lastSynced).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="self-start sm:self-auto flex-shrink-0"
        >
          {syncing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
              Syncing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Data
            </>
          )}
        </Button>
      </div>

      {/* ------------------ TODAY'S BRIEF (Compass) ------------------ */}
      <TodayBriefCard
        onAsk={(prefill) => {
          // Bounce the prefill into the global ChatWidget via a custom event
          // so we don't need to refactor the widget into a context.
          window.dispatchEvent(
            new CustomEvent("council:askPrefill", { detail: { prefill } }),
          );
        }}
      />

      {/* ------------------ METRIC CARDS ------------------ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Portfolio Value */}
        <div className="p-4 sm:p-6 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
          <p className="text-gray-600 dark:text-gray-400 text-sm">TOTAL PORTFOLIO VALUE</p>
          <h2 className="text-2xl sm:text-3xl font-bold mt-2">
            {formatCurrency(portfolioData?.totalValue, displayCurrency)}
          </h2>
        </div>

        {/* Today's Change */}
        <div className="p-4 sm:p-6 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
          <p className="text-gray-600 dark:text-gray-400 text-sm">TODAY'S CHANGE</p>
          <h2 className={`text-2xl sm:text-3xl font-bold mt-2 ${
            (portfolioData?.todayChange || 0) >= 0
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {(portfolioData?.todayChange || 0) >= 0 ? '+' : ''}
            {formatCurrency(Math.abs(portfolioData?.todayChange || 0), displayCurrency)}
            <span className="text-gray-600 dark:text-gray-400 text-lg ml-1">
              ( {(portfolioData?.todayChangePercent || 0) >= 0 ? '+' : ''}
              {(portfolioData?.todayChangePercent || 0).toFixed(2)}% )
            </span>
          </h2>
          <div className="mt-3">
            <AskCouncilButton
              label="Explain today's move"
              prefill={`My portfolio is ${(portfolioData?.todayChangePercent || 0) >= 0 ? "up" : "down"} ${(portfolioData?.todayChangePercent || 0).toFixed(2)}% today. What's driving it and which holdings are responsible?`}
            />
          </div>
        </div>

        {/* Total Return */}
        <div className="p-4 sm:p-6 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
          <p className="text-gray-600 dark:text-gray-400 text-sm">TOTAL RETURN (SINCE INCEPTION)</p>
          <h2 className={`text-2xl sm:text-3xl font-bold mt-2 ${
            (portfolioData?.totalReturn || 0) >= 0
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {(portfolioData?.totalReturn || 0) >= 0 ? '+' : ''}
            {formatCurrency(Math.abs(portfolioData?.totalReturn || 0), displayCurrency)}
            <span className="text-gray-600 dark:text-gray-400 text-lg ml-1">
              ( {(portfolioData?.totalReturnPercent || 0) >= 0 ? '+' : ''}
              {(portfolioData?.totalReturnPercent || 0).toFixed(2)}% )
            </span>
          </h2>
          <div className="mt-3">
            <AskCouncilButton
              label="Break down my returns"
              prefill={`My total return since inception is ${(portfolioData?.totalReturnPercent || 0).toFixed(2)}%. Break this down by holding and tell me what's been a winner vs a drag.`}
            />
          </div>
        </div>

        {/* Risk Score */}
        <div className="p-4 sm:p-6 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
          <p className="text-gray-600 dark:text-gray-400 text-sm">RISK SCORE</p>
          <h2 className="text-2xl sm:text-3xl font-bold mt-2">
            {portfolioData?.riskLabel || 'Moderate'} ({portfolioData?.riskScore || 0}/10)
          </h2>
          <div className="mt-3">
            <AskCouncilButton
              label="Why is my risk this high/low?"
              prefill={`My risk score is ${portfolioData?.riskLabel || 'Moderate'} (${portfolioData?.riskScore || 0}/10). Explain why and what specifically I should change to lower risk without sacrificing too much return.`}
            />
          </div>
        </div>
      </div>

      {/* ------------------ ADVANCED METRICS ------------------ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Beta */}
        <div className="p-4 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-purple-500/10 text-gray-900 dark:text-white">
          <div className="flex items-start justify-between">
            <p className="text-gray-600 dark:text-gray-400 text-xs">BETA (VOLATILITY)</p>
            <AskCouncilButton
              variant="icon"
              prefill={`My portfolio's beta is ${portfolioData?.beta || '0.00'}. Explain what that means for me in plain language and whether it's where I want it to be.`}
            />
          </div>
          <h3 className="text-2xl font-bold mt-1">{portfolioData?.beta || '0.00'}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">vs Market</p>
        </div>

        {/* Sharpe Ratio */}
        <div className="p-4 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-purple-500/10 text-gray-900 dark:text-white">
          <div className="flex items-start justify-between">
            <p className="text-gray-600 dark:text-gray-400 text-xs">SHARPE RATIO</p>
            <AskCouncilButton
              variant="icon"
              prefill={`My Sharpe ratio is ${portfolioData?.sharpeRatio || '0.00'}. Is this good or bad for my portfolio, and what would push it higher?`}
            />
          </div>
          <h3 className="text-2xl font-bold mt-1">{portfolioData?.sharpeRatio || '0.00'}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Risk-Adjusted Return</p>
        </div>

        {/* Max Drawdown */}
        <div className="p-4 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-purple-500/10 text-gray-900 dark:text-white">
          <div className="flex items-start justify-between">
            <p className="text-gray-600 dark:text-gray-400 text-xs">MAX DRAWDOWN</p>
            <AskCouncilButton
              variant="icon"
              prefill={`My max drawdown is ${portfolioData?.maxDrawdown || '0.00'}%. Which position is responsible and what should I do about it?`}
            />
          </div>
          <h3 className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
            {portfolioData?.maxDrawdown || '0.00'}%
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Worst Position</p>
        </div>

        {/* Diversification Score */}
        <div className="p-4 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-purple-500/10 text-gray-900 dark:text-white">
          <div className="flex items-start justify-between">
            <p className="text-gray-600 dark:text-gray-400 text-xs">DIVERSIFICATION</p>
            <AskCouncilButton
              variant="icon"
              prefill={`My diversification score is ${portfolioData?.diversificationScore || '0'}/100. Explain why and give me 2-3 concrete moves to improve it.`}
            />
          </div>
          <h3 className={`text-2xl font-bold mt-1 ${
            (portfolioData?.diversificationScore || 0) >= 70 ? 'text-green-600 dark:text-green-400' :
            (portfolioData?.diversificationScore || 0) >= 40 ? 'text-yellow-600 dark:text-yellow-400' :
            'text-red-600 dark:text-red-400'
          }`}>
            {portfolioData?.diversificationScore || '0'}/100
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Portfolio Balance</p>
        </div>
      </div>

      {/* ------------------ SECTOR ALLOCATION ------------------ */}
      {portfolioData?.sectorAllocation && portfolioData.sectorAllocation.length > 0 && (
        <div className="p-4 sm:p-6 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Sector Allocation</h3>
            <AskCouncilButton
              label="Is this allocation healthy?"
              prefill={(() => {
                const top = [...(portfolioData.sectorAllocation || [])]
                  .sort((a, b) => b.percentage - a.percentage)
                  .slice(0, 3)
                  .map((s) => `${s.sector} ${s.percentage.toFixed(1)}%`)
                  .join(", ");
                return `My sector allocation is dominated by: ${top}. Is this concentration healthy or should I rebalance? What would you change?`;
              })()}
            />
          </div>
          <SectorAllocationChart sectorData={portfolioData.sectorAllocation} />
        </div>
      )}

      {/* ------------------ PERFORMANCE CHART & INSIGHTS ------------------ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Chart Area */}
        <div className="lg:col-span-2 p-4 sm:p-6 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Performance vs S&P 500</h3>
            <div className="flex space-x-2 text-sm">
              {["1M", "3M", "6M", "1Y", "Max"].map((t) => (
                <button
                  key={t}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF" 
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#9CA3AF" 
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#9CA3AF' }}
                  itemStyle={{ color: '#FFF' }}
                  formatter={(value) => `$${value.toLocaleString()}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="portfolio" 
                  stroke="#6366F1" 
                  strokeWidth={2}
                  name="Your Portfolio"
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="sp500" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  name="S&P 500"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500 dark:border-gray-700 dark:text-gray-400">
              No chart data available
            </div>
          )}
        </div>

        {/* AI Insights */}
        <div className="p-4 sm:p-6 rounded-xl bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-semibold">AI Portfolio Insights</h3>
            <span className="text-xs bg-indigo-600 text-white px-2 py-1 rounded">
              BETA
            </span>
          </div>

          {portfolioData?.insights && portfolioData.insights.length > 0 ? (
            <ul className="space-y-3 text-gray-700 dark:text-gray-300 text-sm">
              {portfolioData.insights.map((insight, idx) => (
                <li key={idx}>• {insight}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Add holdings to see AI-generated insights
            </p>
          )}

          <button
            className="mt-4 w-full px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            disabled
          >
            Regenerate Insight (Coming Soon)
          </button>
        </div>
      </div>

      {/* ------------------ HOLDINGS TABLE ------------------ */}
      <div className="p-6 rounded-xl mt-8 bg-white/60 dark:bg-[#1a2332]/50 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-blue-500/10 text-gray-900 dark:text-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Holdings Breakdown</h3>
          <button 
            onClick={exportToCSV}
            className="px-4 py-2 rounded-lg text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>

        {sortedHoldings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b text-sm border-gray-300 text-gray-600 dark:border-gray-700 dark:text-gray-400">
                <tr>
                  <th 
                    className="pb-2 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 text-left"
                    onClick={() => requestSort('symbol')}
                  >
                    SYMBOL {getSortIndicator('symbol')}
                  </th>
                  <th 
                    className="cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 text-left hidden md:table-cell"
                    onClick={() => requestSort('name')}
                  >
                    NAME {getSortIndicator('name')}
                  </th>
                  <th 
                    className="cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 text-right"
                    onClick={() => requestSort('weight')}
                  >
                    WEIGHT {getSortIndicator('weight')}
                  </th>
                  <th 
                    className="cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 text-right hidden lg:table-cell"
                    onClick={() => requestSort('quantity')}
                  >
                    QTY {getSortIndicator('quantity')}
                  </th>
                  <th 
                    className="cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 text-right"
                    onClick={() => requestSort('marketValue')}
                  >
                    VALUE {getSortIndicator('marketValue')}
                  </th>
                  <th 
                    className="cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 text-right"
                    onClick={() => requestSort('unrealizedPLPercent')}
                  >
                    P/L {getSortIndicator('unrealizedPLPercent')}
                  </th>
                  <th className="text-center hidden xl:table-cell">TREND</th>
                </tr>
              </thead>

              <tbody>
                {sortedHoldings.map((holding, idx) => (
                  <tr
                    key={`${holding.symbol}-${idx}`}
                    className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="py-3 font-semibold text-left">
                      <div>
                        <div className="font-bold">{holding.symbol}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 md:hidden">{holding.name}</div>
                      </div>
                    </td>
                    <td className="py-3 hidden md:table-cell text-left">{holding.name}</td>
                    <td className="py-3 text-right">{holding.weight.toFixed(1)}%</td>
                    <td className="py-3 hidden lg:table-cell text-right">{holding.quantity.toFixed(2)}</td>
                    <td className="py-3 text-right">
                      <div>
                        {/* Show value in native currency with the correct symbol */}
                        <div className="font-medium">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: holding.currency || 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(holding.marketValue)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: holding.currency || 'USD',
                            minimumFractionDigits: 2,
                          }).format(holding.price)}
                        </div>
                      </div>
                    </td>
                    <td className={`py-3 font-semibold text-right ${
                      holding.unrealizedPLPercent >= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {holding.unrealizedPLPercent >= 0 ? '+' : ''}
                      {holding.unrealizedPLPercent.toFixed(2)}%
                    </td>
                    <td className="py-3 hidden xl:table-cell">
                      <MiniSparkline 
                        trend={holding.unrealizedPLPercent >= 0 ? "up" : "down"} 
                        value={holding.unrealizedPLPercent} 
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              No holdings found. Link your brokerage account to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
